import React, { useRef } from "react";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/UTDT_Logo.png";
const pdf = "/img/pdf1.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";

ChartJS.register(ArcElement, Tooltip, Legend, Title);

export default function TransportationTable21({ data }) {
  const pdfRef = useRef();

  if (!data || data.length === 0) {
    return <p>No data available</p>;
  }

  /* =============================
     PROCESS DATA
  ============================== */

  const processed = data
    .map((d) => ({
      category: d.category,
      value: Number(d.emp_count || 0),
    }))
    .sort((a, b) => b.value - a.value);

  const total = processed.reduce((sum, r) => sum + r.value, 0);

  const withPercent = processed.map((r) => ({
    ...r,
    percent: total > 0 ? ((r.value / total) * 100).toFixed(1) : 0,
  }));

  const leading = withPercent[0];

  /* =============================
     CHART DATA
  ============================== */

  const chartData = {
    labels: withPercent.map((r) => r.category),
    datasets: [
      {
        data: withPercent.map((r) => r.value),
        backgroundColor: [
          "#3b82f6",
          "#f59e0b",
          "#10b981",
          "#ef4444",
          "#8b5cf6",
          "#14b8a6",
        ],
        borderWidth: 2,
      },
    ],
  };

  const options = {
    responsive: true,
    cutout: "65%",
    plugins: {
      legend: {
        position: "bottom",
      },
      title: {
        display: true,
        text: "📊 Race Composition (Employment)",
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return context.parsed.toLocaleString("en-US");
          },
        },
      },
    },
  };

  /* =============================
     PDF EXPORT
  ============================== */

   const downloadPDF = () => {
     const input = pdfRef.current;
     html2canvas(input, {
       scale: 2,
       useCORS: true,
       backgroundColor: "#ffffff",
       scrollY: -window.scrollY,
     }).then((canvas) => {
       const imgData = canvas.toDataURL("image/png");
       const pdf = new jsPDF("p", "mm", "a4");
       const pageWidth = pdf.internal.pageSize.getWidth();
       const pageHeight = pdf.internal.pageSize.getHeight();
       const imgWidth = pageWidth - 10;
       const imgHeight = (canvas.height * imgWidth) / canvas.width;

       let heightLeft = imgHeight;
       let position = 0;
       let pageNumber = 1;

       const addHeader = () => {
         const logoWidth = 20; // ideal header width
         const logoHeight = 20; // adjust ratio if needed

         pdf.addImage(utahData, "PNG", 70, 10, logoWidth, logoHeight);
         pdf.setFontSize(26);
         pdf.text("UTahData.org", 95, 22);
         pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
         pdf.line(10, 35, 200, 35);
       };

       const addFooter = (pageNumber) => {
         pdf.setFontSize(9);
         const footerY = pageHeight - 10;
         pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
         pdf.text(
           `Downloaded on: ${new Date().toLocaleDateString()}`,
           10,
           footerY,
         );
         pdf.text(
           `Page ${pageNumber}`,
           pageWidth - 10,
           footerY,
           null,
           null,
           "right",
         );
       };

       // First page
       addHeader();
       pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
       heightLeft -= pageHeight - 32;

       // Add more pages if needed
       while (heightLeft > 0) {
         pdf.addPage();
         pageNumber++;
         addHeader();
         position = heightLeft - imgHeight;
         pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
         heightLeft -= pageHeight - 32;
         addFooter(pageNumber);
       }

       // Final footer
       // ===== FINAL FOOTER SAFE AREA =====

       const footerTopY = pageHeight - 40; // start footer 40mm from bottom
       const footerContentY = pageHeight - 28; // logos row
       const footerBottomTextY = pageHeight - 10; // bottom text row

       // Top Divider Line
       pdf.line(10, footerTopY, pageWidth - 10, footerTopY);

       // UtahData logo
       const utahWidth = 18;
       const utahHeight = 18;
       pdf.addImage(
         utahData,
         "PNG",
         20,
         footerContentY - 8,
         utahWidth,
         utahHeight,
       );

       // Built by
       pdf.setFontSize(10);
       pdf.text("Built by", 45, footerContentY);

       // BODEX logo
       const bodexWidth = 36;
       const bodexHeight = bodexWidth * 0.3;
       pdf.addImage(
         bodex,
         "PNG",
         60,
         footerContentY - 6,
         bodexWidth,
         bodexHeight,
       );

       // Using text
       pdf.text("using", 102, footerContentY);

       // KPI logo
       pdf.addImage(kpi, "PNG", 118, footerContentY - 8, 16, 16);

       // Version
       pdf.text("20250916.v3.198", 140, footerContentY);

       // Bottom Info Line
       pdf.setFontSize(8);
       pdf.text(`From: ${window.location.href}`, 10, footerBottomTextY);
       pdf.text(
         `Downloaded on: ${new Date().toLocaleDateString()}`,
         pageWidth - 10,
         footerBottomTextY,
         null,
         null,
         "right",
       );

       pdf.save("race_composition_snapshot.pdf");
     });
   };

  /* =============================
     UI
  ============================== */

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Race Composition Snapshot
        </h2>

        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Total Employment</p>
            <p className="text-xl font-bold text-blue-800">
              {total.toLocaleString("en-US")}
            </p>
          </div>

          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Leading Category</p>
            <p className="text-xl font-bold text-green-800">
              {leading.category}
            </p>
          </div>

          <div className="p-4 bg-purple-100 rounded-xl shadow">
            <p className="text-sm text-gray-600">Leading Share %</p>
            <p className="text-xl font-bold text-purple-800">
              {leading.percent}%
            </p>
          </div>
        </div>

        {/* TABLE + DOUGHNUT */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
          {/* TABLE */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Category</th>
                  <th className="p-2">Employment</th>
                  <th className="p-2">Share %</th>
                </tr>
              </thead>
              <tbody>
                {withPercent.map((row, idx) => (
                  <tr
                    key={idx}
                    className={`${
                      idx === 0
                        ? "bg-green-100 font-semibold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2">{row.category}</td>
                    <td className="p-2">{row.value.toLocaleString("en-US")}</td>
                    <td className="p-2">{row.percent}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* DOUGHNUT */}
          <div className="relative w-full max-w-[400px] h-[400px] mx-auto bg-white rounded-lg shadow-md p-4 flex items-center justify-center">
            <Doughnut data={chartData} options={options} />
            <div className="absolute text-center">
              <p className="text-2xl font-bold text-blue-700">
                {leading.percent}%
              </p>
              <p className="text-sm text-gray-600">Largest Share</p>
            </div>
          </div>
        </div>
      </div>

      {/* DOWNLOAD BUTTON */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
